import React, { FC, Fragment } from 'react';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { DateTime } from 'luxon';

import { fallbackText } from '@pharmaplan/common';
import fr from '@pharmaplan/common/localization/fr';
import en from '@pharmaplan/common/localization/en';

import InvoiceTimePicker from '../InvoiceTimePicker';
import { IInvoiceStateBookings } from '../InvoiceLayout/InvoiceLayout';

import { IInvoiceTable, InvoiceCellType } from './helper';
import style from './style';

const InvoiceTable: FC<IInvoiceTable> = ({
  table,
  invoiceState,
  setInvoiceState,
}) => {
  const handleRow = (
    value: string | DateTime | number,
    id: string,
    dateType?: string | number,
    cellType = InvoiceCellType.text,
  ) => {
    const currentTime = invoiceState?.[id]?.[
        (dateType ?? 'startDate') as keyof IInvoiceStateBookings
    ];
    const handleTimeChange = (time: DateTime | null) => {
      if (dateType) {
        setInvoiceState((prev) =>
          ({
            ...prev,
            [id]: {
              ...prev[id],
              [dateType]: time,
              changed: true,
            },
          }));
      }
    };

    if (cellType === InvoiceCellType.timePicker) {
      return (
        <TableCell sx={[style.row, style.rowTime]}>
          <InvoiceTimePicker
            currentTime={currentTime as DateTime}
            handleTimeChange={handleTimeChange}
          />
        </TableCell>
      );
    }

    return (
      <TableCell sx={style.row} align="center">
        {(value as string) || fallbackText}
      </TableCell>
    );
  };

  const hours = `${fr.hours}/${en.hours}`;

  return (
    <Paper sx={style.container}>
      <TableContainer sx={style.tableContainer}>
        <Table
          aria-label="sticky table"
          sx={{ borderCollapse: 'collapse' }}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <TableCell sx={style.cellStyle} />
              <TableCell sx={style.cellStyle} />
              <TableCell align="center" colSpan={3} sx={style.header}>
                {hours}
              </TableCell>
              <TableCell sx={style.cellStyle} />
              <TableCell sx={style.cellStyle} />
            </TableRow>

            <TableRow>
              {table.headers.map((item) => {
                const { key, label } = item ?? {};
                return (
                  <TableCell
                    key={key}
                    sx={[style.header, style.labelStyle]}
                    align="center"
                  >
                    {label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((rowItems) => {
              const { data: rowData, key: rowKey } = rowItems ?? {};

              return (
                <TableRow key={rowKey}>
                  {rowData.map((item) => {
                    const { value, key, type } = item ?? {};

                    return (
                      <Fragment key={key}>
                        {handleRow(value, `${rowKey}`, key, type)}
                      </Fragment>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default InvoiceTable;
